import { DOCUMENT, Location, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import {
  DEFAULT_LANG,
  SUPPORTED_LANGUAGES,
  isSupportedLang,
  type Lang,
} from '../data/languages.config';
import { langOfPath, localePath } from '../utils/locale-path';

const STORAGE_KEY = 'avyna.lang';

/**
 * Owns the active language.
 *
 * `lang` only changes once the translation file has actually loaded, so
 * anything computed from it — content, page metadata — never renders against
 * a half-loaded dictionary. English is the fallback, which means a missing
 * French key shows English text rather than a raw key.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly supported = SUPPORTED_LANGUAGES;

  private readonly _lang = signal<Lang>(DEFAULT_LANG);
  /** The language whose dictionary is loaded and in use. */
  readonly lang = this._lang.asReadonly();

  /** Awaited by an app initializer, so the first paint is already translated. */
  async init(): Promise<void> {
    this.translate.addLangs([...SUPPORTED_LANGUAGES.map((l) => l.code)]);
    this.translate.setFallbackLang(DEFAULT_LANG);

    // Location.path(), not Router.url: the initial navigation runs after initializers, so the
    // router still holds '/'. During a prerender it is the route being rendered.
    await this.apply(langOfPath(this.location.path(true) || '/'));

    this.followUrl();
  }

  /** Switches language by going to this page's address in it. */
  async use(lang: Lang): Promise<void> {
    if (lang === this._lang()) return;

    this.remember(lang);
    await this.apply(lang);
    await this.router.navigateByUrl(localePath(this.location.path(true) || '/', lang));
  }

  private async apply(lang: Lang): Promise<void> {
    await firstValueFrom(this.translate.use(lang));
    this._lang.set(lang);
    this.document.documentElement.lang = lang;
  }

  /** Keeps the dictionary in step with the address; a stored choice is honoured only at the root. */
  private followUrl(): void {
    let atEntry = true;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        const entry = atEntry;
        atEntry = false;

        const target = entry ? this.entryRedirectTarget(url) : null;
        if (target) {
          void this.router.navigateByUrl(localePath('/', target), { replaceUrl: true });
          return;
        }

        const lang = langOfPath(url);
        if (lang !== this._lang()) void this.apply(lang);
      });
  }

  /** The language whose root a first arrival at `url` is sent on to, or null; such an arrival is not a page. */
  entryRedirectTarget(url: string): Lang | null {
    if (!this.isBrowser || url !== '/') return null;

    const stored = this.stored();
    return stored && stored !== DEFAULT_LANG ? stored : null;
  }

  private stored(): Lang | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);

      return isSupportedLang(value) ? value : null;
    } catch {
      return null;
    }
  }

  private remember(lang: Lang): void {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable — the choice simply does not persist */
    }
  }
}
